/**
 * Config ZIP Handler
 * 
 * Utility functions for exporting and importing wedding configuration packages.
 * Handles creation and extraction of ZIP files containing theme, logo, and floorplan configs.
 */

import JSZip from 'jszip';
import { loadTheme, loadLogo, loadTabs, DEFAULT_FONT } from './themeStorage';

/**
 * Convert base64 data URL to blob
 * @param {string} dataUrl - Base64 data URL
 * @returns {Blob} - Blob object
 */
const dataUrlToBlob = (dataUrl) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/**
 * Get file extension from base64 data URL or filename
 * @param {string} dataUrl - Base64 data URL or filename
 * @returns {string} - File extension (e.g., 'png', 'jpg', 'webp', 'pdf')
 */
const getFileExtension = (dataUrl) => {
  if (dataUrl.startsWith('data:')) {
    // Extract from MIME type
    const mime = dataUrl.split(',')[0].match(/:(.*?);/)?.[1] || '';
    if (mime.includes('png')) return 'png';
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
    if (mime.includes('webp')) return 'webp';
    if (mime.includes('svg')) return 'svg';
    if (mime.includes('pdf')) return 'pdf';
    return 'png'; // Default fallback
  } else {
    // Extract from filename
    const match = dataUrl.match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : 'png';
  }
};

/**
 * Export all configurations as a single ZIP file
 * @returns {Promise<Blob>} - ZIP file blob
 */
export const exportConfigAsZip = async () => {
  const zip = new JSZip();

  // Load current configurations from localStorage
  const themeData = loadTheme();
  const logoData = loadLogo();
  const tabsData = loadTabs();

  // Create theme config JSON
  const themeConfig = {
    type: 'theme',
    colors: themeData.colors || themeData,
    font: themeData.font || DEFAULT_FONT
  };
  zip.file('wedding-theme-config.json', JSON.stringify(themeConfig, null, 2));

  // Handle logo config and image
  let logoFileName = 'logo.png';
  if (logoData.logo) {
    if (logoData.logo.startsWith('data:')) {
      // Base64 image - convert to file
      const ext = getFileExtension(logoData.logo);
      logoFileName = `logo.${ext}`;
      const logoBlob = dataUrlToBlob(logoData.logo);
      zip.file(logoFileName, logoBlob);
    } else {
      // URL or file path - try to fetch it
      try {
        const response = await fetch(logoData.logo);
        const blob = await response.blob();
        const ext = getFileExtension(logoData.logo);
        logoFileName = `logo.${ext}`;
        zip.file(logoFileName, blob);
      } catch (error) {
        console.error('Failed to fetch logo file:', error);
        logoFileName = logoData.logo.split('/').pop() || 'logo.png';
      }
    }
  }

  const logoConfig = {
    type: 'logo',
    weddingText: logoData.text || 'Wedding of Groom & Bride',
    logoFileName: logoFileName
  };
  zip.file('wedding-logo-config.json', JSON.stringify(logoConfig, null, 2));

  // Handle tabs config and floorplan image
  let floorplanFileName = 'floorplan.png';
  if (tabsData.floorplan) {
    if (tabsData.floorplan.startsWith('data:')) {
      // Base64 image - convert to file
      const ext = getFileExtension(tabsData.floorplan);
      floorplanFileName = `floorplan.${ext}`;
      const floorplanBlob = dataUrlToBlob(tabsData.floorplan);
      zip.file(floorplanFileName, floorplanBlob);
    } else {
      // URL or file path - try to fetch it
      try {
        const response = await fetch(tabsData.floorplan);
        const blob = await response.blob();
        const ext = getFileExtension(tabsData.floorplan);
        floorplanFileName = `floorplan.${ext}`;
        zip.file(floorplanFileName, blob);
      } catch (error) {
        console.error('Failed to fetch floorplan file:', error);
        floorplanFileName = tabsData.floorplan.split('/').pop() || 'floorplan.png';
      }
    }
  }

  // Handle menu image/PDF (optional)
  let menuFileName = null;
  if (tabsData.menu) {
    if (tabsData.menu.startsWith('data:')) {
      // Base64 data - convert to file
      const ext = getFileExtension(tabsData.menu);
      menuFileName = `menu.${ext}`;
      const menuBlob = dataUrlToBlob(tabsData.menu);
      zip.file(menuFileName, menuBlob);
    } else {
      // URL or file path - try to fetch it
      try {
        const response = await fetch(tabsData.menu);
        const blob = await response.blob();
        const ext = getFileExtension(tabsData.menu);
        menuFileName = `menu.${ext}`;
        zip.file(menuFileName, blob);
      } catch (error) {
        console.error('Failed to fetch menu file:', error);
        menuFileName = tabsData.menu.split('/').pop() || null;
      }
    }
  }

  const tabsConfig = {
    type: 'tabs',
    floorplanFileName: floorplanFileName,
    menuFileName: menuFileName,
    showVenueTab: tabsData.showVenueTab !== false,
    showMediaFilter: tabsData.showMediaFilter !== false,
    showGuestbookTab: tabsData.showGuestbookTab !== false,
    showQAndATab: tabsData.showQAndATab !== false
  };
  zip.file('wedding-tabs-config.json', JSON.stringify(tabsConfig, null, 2));

  // Generate ZIP blob
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  return zipBlob;
};

/**
 * Download ZIP blob as a file
 * @param {Blob} zipBlob - ZIP file blob
 * @param {string} filename - Filename for download
 * @returns {Promise<void>} - Resolves after download is initiated
 */
export const downloadZip = (zipBlob, filename = 'wedding-config.zip') => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Wait a bit before resolving to ensure download dialog has appeared
    setTimeout(() => {
      URL.revokeObjectURL(url);
      resolve();
    }, 100);
  });
};

/**
 * Validate ZIP file structure
 * @param {JSZip} zip - JSZip instance
 * @returns {Object} - Validation result with success flag and error message
 */
const validateZipStructure = (zip) => {
  const requiredFiles = [
    'wedding-theme-config.json',
    'wedding-logo-config.json',
    'wedding-tabs-config.json'
  ];

  const missingFiles = requiredFiles.filter(file => !zip.files[file]);
  
  if (missingFiles.length > 0) {
    return {
      success: false,
      error: `Missing required files: ${missingFiles.join(', ')}`
    };
  }

  return { success: true };
};

/**
 * Import configuration from ZIP file
 * @param {File} zipFile - ZIP file from file input
 * @returns {Promise<Object>} - Imported configuration data
 */
export const importConfigFromZip = async (zipFile) => {
  try {
    const zip = await JSZip.loadAsync(zipFile);

    // Validate ZIP structure
    const validation = validateZipStructure(zip);
    if (!validation.success) {
      throw new Error(validation.error);
    }

    // Extract theme config
    const themeConfigText = await zip.file('wedding-theme-config.json').async('text');
    const themeConfig = JSON.parse(themeConfigText);
    if (themeConfig.type !== 'theme' || !themeConfig.colors) {
      throw new Error('Invalid theme config format');
    }

    // Extract logo config
    const logoConfigText = await zip.file('wedding-logo-config.json').async('text');
    const logoConfig = JSON.parse(logoConfigText);
    if (logoConfig.type !== 'logo' || !logoConfig.logoFileName) {
      throw new Error('Invalid logo config format');
    }

    // Extract logo image file
    const logoFile = zip.file(logoConfig.logoFileName);
    if (!logoFile) {
      throw new Error(`Logo image file not found: ${logoConfig.logoFileName}`);
    }
    // Determine MIME type from filename
    const logoExt = logoConfig.logoFileName.split('.').pop()?.toLowerCase();
    let logoMimeType = 'image/png';
    if (logoExt === 'jpg' || logoExt === 'jpeg') logoMimeType = 'image/jpeg';
    else if (logoExt === 'webp') logoMimeType = 'image/webp';
    else if (logoExt === 'svg') logoMimeType = 'image/svg+xml';
    
    const logoData = await logoFile.async('uint8array');
    const logoBlob = new Blob([logoData], { type: logoMimeType });
    const logoDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(logoBlob);
    });

    // Extract tabs config
    const tabsConfigText = await zip.file('wedding-tabs-config.json').async('text');
    const tabsConfig = JSON.parse(tabsConfigText);
    if (tabsConfig.type !== 'tabs' || !tabsConfig.floorplanFileName) {
      throw new Error('Invalid tabs config format');
    }

    // Extract floorplan image file
    const floorplanFile = zip.file(tabsConfig.floorplanFileName);
    if (!floorplanFile) {
      throw new Error(`Floorplan image file not found: ${tabsConfig.floorplanFileName}`);
    }
    // Determine MIME type from filename
    const floorplanExt = tabsConfig.floorplanFileName.split('.').pop()?.toLowerCase();
    let floorplanMimeType = 'image/png';
    if (floorplanExt === 'jpg' || floorplanExt === 'jpeg') floorplanMimeType = 'image/jpeg';
    else if (floorplanExt === 'webp') floorplanMimeType = 'image/webp';
    
    const floorplanData = await floorplanFile.async('uint8array');
    const floorplanBlob = new Blob([floorplanData], { type: floorplanMimeType });
    const floorplanDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(floorplanBlob);
    });

    // Extract menu file (optional)
    let menuDataUrl = null;
    if (tabsConfig.menuFileName) {
      const menuFile = zip.file(tabsConfig.menuFileName);
      if (menuFile) {
        // Determine MIME type from filename
        const menuExt = tabsConfig.menuFileName.split('.').pop()?.toLowerCase();
        let menuMimeType = 'application/octet-stream';
        if (menuExt === 'pdf') menuMimeType = 'application/pdf';
        else if (menuExt === 'png') menuMimeType = 'image/png';
        else if (menuExt === 'jpg' || menuExt === 'jpeg') menuMimeType = 'image/jpeg';
        else if (menuExt === 'webp') menuMimeType = 'image/webp';
        
        // Get raw data and create blob with correct MIME type
        const menuData = await menuFile.async('uint8array');
        const menuBlob = new Blob([menuData], { type: menuMimeType });
        menuDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(menuBlob);
        });
      }
    }

    // Return extracted data
    return {
      success: true,
      theme: {
        colors: themeConfig.colors,
        font: themeConfig.font || DEFAULT_FONT
      },
      logo: {
        image: logoDataUrl,
        text: logoConfig.weddingText || 'Wedding of Groom & Bride'
      },
      tabs: {
        image: floorplanDataUrl,
        menu: menuDataUrl,
        showVenueTab: tabsConfig.showVenueTab !== false,
        showMediaFilter: tabsConfig.showMediaFilter !== false,
        showGuestbookTab: tabsConfig.showGuestbookTab !== false,
        showQAndATab: tabsConfig.showQAndATab !== false
      }
    };
  } catch (error) {
    console.error('Failed to import config from ZIP:', error);
    return {
      success: false,
      error: error.message || 'Failed to parse ZIP file'
    };
  }
};
